import type { IAuthModel } from "@models/auth/IAuthModel";
import {
  DEEPLINK_TYPE,
  type SharedLinkDto,
} from "@models/data/sharedLinkDto";
import type { ProjectInfo } from "@models/data/projectInfo";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type {
  IDeepLinksHandlerModel,
  ResolveShareLinkResult,
  ShareLinkRequest,
  ShareLinkTarget,
} from "./IDeepLinksHandlerModel";

/**
 * `DeepLinksHandlerModel` — the browser half of Unity's
 * `Models/DeepLinksHandlerModel.cs`.
 *
 * Unity receives `c3d://query?sl&act&prj` from the website launcher. The web
 * receives the same query directly at its `/query` route; auth deferral is the
 * router's existing `/login?from=<fullPath>` bounce rather than a private
 * `m_deferredLink`.
 *
 * Resolution order mirrors `ProcessDeeplink` (`:326-397`):
 *
 *  1. Old format, or `act` is my account: point-read the link in my account.
 *  2. New cross-account format: the refreshed project catalog must already
 *     contain `(act, prj)` via `getSharedLinksPipeline`, then point-read the
 *     real link from that project's owning account.
 *  3. Legacy fallback: find a catalog project whose `sharedLinkId == sl`.
 *
 * One deliberate hardening against a tampered URL: the web verifies the
 * resolved row's `ProjectID` and the catalog project's `customerId` against
 * `prj` / `act` before navigating. Unity first navigates a synthetic project
 * link and validates the real row afterwards, which can briefly open the
 * wrong project when those query parameters disagree. The link row remains
 * the grant; this only fails closed sooner.
 *
 * Annotation/sketch/measurement share links are OUT OF SCOPE for AI-1341.
 * Like Unity's NotFound result, they still open the project/scene and return
 * the exact resource-missing copy for the toast layer.
 */
export class DeepLinksHandlerModel implements IDeepLinksHandlerModel {
  constructor(
    private readonly m_auth: IAuthModel,
    private readonly m_storage: IStorageModel,
    private readonly m_projects: IProjectListModel,
  ) {}

  async resolveAsync(
    request: ShareLinkRequest,
    signal?: AbortSignal,
  ): Promise<ResolveShareLinkResult> {
    const currentCustomerId = this.m_auth.currentUser?.customerId ?? "";
    if (!currentCustomerId || !request.sharedLinkId) return notFound();

    // `customer.Projects` is expected to have finished loading before Unity
    // processes a link. The route can be entered directly after login, so the
    // web performs that prerequisite explicitly and refreshes the external
    // share pipeline too.
    try {
      await this.m_projects.refreshAsync(signal);
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") throw err;
      return notFound();
    }

    const isNewFormat = !!request.accountId && !!request.projectId;
    let link: SharedLinkDto | null = null;
    let project: ProjectInfo | null = null;

    try {
      // Try the currently active account first (`:338-347`).
      if (!isNewFormat || request.accountId === currentCustomerId) {
        link = await this.m_storage.getSharedLink(
          currentCustomerId,
          request.sharedLinkId,
          signal,
        );
        if (link) {
          const projectId = link.ProjectID || request.projectId;
          project = projectId ? this.m_projects.getById(projectId) : null;
        }
      }

      // The link might be from another company (`:349-371`). It is only
      // considered after the project appeared in the caller's refreshed
      // catalog; knowing an account/project/link triple is not itself access.
      if (!link && isNewFormat) {
        const candidate = this.m_projects.getById(request.projectId!);
        if (candidate && this.projectBelongsToAccount(candidate, request.accountId!)) {
          const ownerCustomerId = candidate.customerId || request.accountId!;
          const candidateLink = await this.m_storage.getSharedLink(
            ownerCustomerId,
            request.sharedLinkId,
            signal,
          );
          if (
            candidateLink
            && (!candidateLink.ProjectID || candidateLink.ProjectID === candidate.id)
          ) {
            link = candidateLink;
            project = candidate;
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") throw err;
      return notFound();
    }

    if (link && project && this.matchesRequest(link, project, request)) {
      return targetFor(link, project);
    }

    // Legacy fallback (`:375-389`). Upstream notes this likely does not work
    // for internal links because their id differs from the invite id, but
    // keeps it for old rows. The web does the same.
    if (!isNewFormat) {
      const legacyProject = this.m_projects.projects.find(
        (item) => item.sharedLinkId === request.sharedLinkId,
      );
      if (legacyProject) {
        return {
          ok: true,
          target: {
            project: legacyProject,
            sceneId: null,
            notice: null,
          },
        };
      }
    }

    return notFound();
  }

  private projectBelongsToAccount(project: ProjectInfo, accountId: string): boolean {
    const customerId = project.customerId || this.m_auth.currentUser?.customerId || "";
    return customerId === accountId;
  }

  private matchesRequest(
    link: SharedLinkDto,
    project: ProjectInfo,
    request: ShareLinkRequest,
  ): boolean {
    if (link.ProjectID && link.ProjectID !== project.id) return false;
    if (request.projectId && request.projectId !== project.id) return false;
    if (request.accountId && !this.projectBelongsToAccount(project, request.accountId)) {
      return false;
    }
    return true;
  }
}

function targetFor(
  link: SharedLinkDto,
  project: ProjectInfo,
): ResolveShareLinkResult {
  const type = link.LinkType ?? 0;
  const base: ShareLinkTarget = {
    project,
    sceneId: link.SceneID || null,
    notice: null,
  };

  switch (type) {
    case DEEPLINK_TYPE.ProjectLink:
      return { ok: true, target: { ...base, sceneId: null } };
    case DEEPLINK_TYPE.SceneLink:
      return { ok: true, target: base };
    case DEEPLINK_TYPE.AnnotationLink:
    case DEEPLINK_TYPE.SketchLink:
      return {
        ok: true,
        target: {
          ...base,
          notice: "The annotation/sketch you are trying to load no longer exists",
        },
      };
    case DEEPLINK_TYPE.MeasurementLink:
      return {
        ok: true,
        target: {
          ...base,
          notice: "The measurement you are trying to load no longer exists",
        },
      };
    default:
      return notFound();
  }
}

function notFound(): ResolveShareLinkResult {
  return {
    ok: false,
    message: "The resource you are trying to load does not exist on this account.",
  };
}
